import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiMessageCircle, FiPhone, FiVideo, FiArrowRight, FiCheck, FiLayers, FiSettings
} from 'react-icons/fi';
import logo from '../../Images/logo.png';
import styles from './AIAgentsPage.module.css';

const AIAgentsPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.container}>
      {/* Background ambient glows */}
      <div className={styles.ambientGlows}>
        <div className={styles.glow1}></div>
        <div className={styles.glow2}></div>
        <div className={styles.glow3}></div>
      </div>

      {/* 🟦 1. HEADER / NAVIGATION */}
      <header className={styles.header} style={{ flex: "none" }}>
        <div className={styles.headerInner}>
          <div className={styles.logoArea} onClick={() => navigate('/')}>
            <img src={logo} alt="SaleSense Logo" className={styles.logoImg} />
            <span className={styles.logoText}>Sailsense <span className={styles.logoCyan}>AI</span></span>
          </div>
          <nav className={styles.navLinks}>
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>Home</a>
            <a href="/ai-agents" className={styles.activeLink} onClick={(e) => { e.preventDefault(); }}>AI Agents</a>
            <a href="/platform" onClick={(e) => { e.preventDefault(); navigate('/platform'); }}>Platform</a>
            <a href="/sales-operations" onClick={(e) => { e.preventDefault(); navigate('/sales-operations'); }}>Sales Operations</a>
            <a href="/solutions" onClick={(e) => { e.preventDefault(); navigate('/solutions'); }}>Solutions</a>
            <a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }}>Pricing</a>
            <a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>Contact</a>
          </nav>
          <div className={styles.headerActions}>
            <button className={styles.trialBtn} onClick={() => navigate('/login')}>
              login <FiArrowRight />
            </button>
          </div>
        </div>
      </header>

      {/* Grid line overlay */}
      <div className={styles.gridOverlay}></div>

      {/* 🟦 2. MEET YOUR AI WORKFORCE */}
      <section className={styles.sectionBlock}>
        <div className={styles.sectionCenter}>
          <div className={styles.badgeCyan}>
            AI Workforce
          </div>
          <h1 className={styles.sectionCenterTitle}>Meet Your AI Workforce</h1>
          <p className={styles.sectionSubtitle}>
            Sailsense AI combines intelligent conversational, voice, and video agents that work together to capture, engage, and convert leads 24/7.
          </p>
        </div>

        <div className={styles.workforceGrid}>
          {/* Conversational AI */}
          <div className={styles.workforceCard}>
            <div className={styles.workforceCardHeader}>
              <div className={`${styles.workforceIconBox} ${styles.conversational}`}>
                <FiMessageCircle />
              </div>
              <div className={styles.workforceTitleBox}>
                <span className={styles.workforceTag}>CONVERSATIONAL AI</span>
                <h4 className={styles.workforceTitle}>Sailsense Chatbot</h4>
              </div>
            </div>
            <p className={styles.workforceDesc}>
              Multi-channel conversational AI for Website and WhatsApp.
            </p>
            <div className={styles.capabilitiesContainer}>
              <span className={styles.capabilitiesHeading}>CAPABILITIES</span>
              <div className={styles.capabilitiesGrid}>
                <div className={styles.capabilitiesCol}>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Instant lead capture
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Variant & pricing assistance
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> FAQ automation
                  </div>
                </div>
                <div className={styles.capabilitiesCol}>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Vehicle recommendations
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Test drive scheduling
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> WhatsApp engagement
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Voice AI */}
          <div className={styles.workforceCard}>
            <div className={styles.workforceCardHeader}>
              <div className={`${styles.workforceIconBox} ${styles.voice}`}>
                <FiPhone />
              </div>
              <div className={styles.workforceTitleBox}>
                <span className={styles.workforceTag}>VOICE AI</span>
                <h4 className={styles.workforceTitle}>ROVIA Voice Agent</h4>
              </div>
            </div>
            <p className={styles.workforceDesc}>
              Your AI-powered sales caller that follows up on every lead within seconds.
            </p>
            <div className={styles.capabilitiesContainer}>
              <span className={styles.capabilitiesHeading}>CAPABILITIES</span>
              <div className={styles.capabilitiesGrid}>
                <div className={styles.capabilitiesCol}>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Instant lead calling
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Appointment scheduling
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Missed lead recovery
                  </div>
                </div>
                <div className={styles.capabilitiesCol}>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Lead qualification
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Follow-up automation
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Smart call routing
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Video AI */}
          <div className={styles.workforceCard}>
            <div className={styles.workforceCardHeader}>
              <div className={`${styles.workforceIconBox} ${styles.video}`}>
                <FiVideo />
              </div>
              <div className={styles.workforceTitleBox}>
                <span className={styles.workforceTag}>VIDEO AI</span>
                <h4 className={styles.workforceTitle}>AIMEE Video Presenter</h4>
              </div>
            </div>
            <p className={styles.workforceDesc}>
              Interactive AI video presenter for websites and landing pages.
            </p>
            <div className={styles.capabilitiesContainer}>
              <span className={styles.capabilitiesHeading}>CAPABILITIES</span>
              <div className={styles.capabilitiesGrid}>
                <div className={styles.capabilitiesCol}>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Welcome website visitors
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Showcase vehicle features
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Guide visitors to conversion
                  </div>
                </div>
                <div className={styles.capabilitiesCol}>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Explain products & offers
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Increase engagement
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Workflow AI */}
          <div className={styles.workforceCard}>
            <div className={styles.workforceCardHeader}>
              <div className={`${styles.workforceIconBox} ${styles.workflow}`}>
                <FiLayers />
              </div>
              <div className={styles.workforceTitleBox}>
                <span className={styles.workforceTag}>WORKFLOW AI</span>
                <h4 className={styles.workforceTitle}>Automation Engine</h4>
              </div>
            </div>
            <p className={styles.workforceDesc}>
              Automates repetitive sales tasks across channels.
            </p>
            <div className={styles.capabilitiesContainer}>
              <span className={styles.capabilitiesHeading}>CAPABILITIES</span>
              <div className={styles.capabilitiesGrid}>
                <div className={styles.capabilitiesCol}>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> WhatsApp automation
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> CRM sync alerts
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Follow-up sequences
                  </div>
                </div>
                <div className={styles.capabilitiesCol}>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Email campaigns
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Lead nurturing workflows
                  </div>
                  <div className={styles.capabilityItem}>
                    <span className={styles.cyanBullet}></span> Smart notifications
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 🟦 3. HOW THEY WORK TOGETHER */}
      <section className={styles.sectionBlock} style={{ borderTop: "1px solid rgba(255, 255, 255, 0.05)" }}>
        <div className={styles.sectionCenter}>
          <div className={styles.badgeCyan}>
            How They Work Together
          </div>
          <h2 className={styles.sectionCenterTitle}>One pipeline. Four AI agents.</h2>
        </div>

        <div className={styles.pipelineRow}>
          <div className={styles.pipelineCard}>
            <span className={styles.pipelineNumber}>01</span>
            <span className={styles.pipelineLabel}>Chatbot captures</span>
          </div>
          <div className={styles.pipelineArrow}>
            <FiArrowRight />
          </div>
          <div className={styles.pipelineCard}>
            <span className={styles.pipelineNumber}>02</span>
            <span className={styles.pipelineLabel}>ROVIA calls</span>
          </div>
          <div className={styles.pipelineArrow}>
            <FiArrowRight />
          </div>
          <div className={styles.pipelineCard}>
            <span className={styles.pipelineNumber}>03</span>
            <span className={styles.pipelineLabel}>AIMEE engages</span>
          </div>
          <div className={styles.pipelineArrow}>
            <FiArrowRight />
          </div>
          <div className={styles.pipelineCard}>
            <span className={styles.pipelineNumber}>04</span>
            <span className={styles.pipelineLabel}>Automation closes</span>
          </div>
        </div>

        {/* Deploy AI sales team CTA */}
        <div className={styles.deployCtaBox}>
          <h3 className={styles.deployCtaHeading}>Ready to deploy your AI sales team?</h3>
          <div className={styles.deployCtaButtons}>
            <button className={styles.heroBtnCyan} onClick={() => navigate('/login')}>
              Start Free Trial <FiArrowRight />
            </button>
            <button className={styles.deployPricingBtn} onClick={() => navigate('/login')}>
              See Pricing
            </button>
          </div>
        </div>
      </section>

      {/* 🟦 4. FOOTER */}
      <footer className={styles.footerSection}>
        <div className={styles.footerInner}>
          {/* Brand area left */}
          <div className={styles.footerBrandColumn}>
            <div className={styles.footerBrandLogo} onClick={() => navigate('/')}>
              <img src={logo} alt="SaleSense Logo" className={styles.footerLogoImg} />
              <span>Sailsense AI</span>
            </div>
            <p className={styles.footerBrandText}>
              AI Chat, Voice and Video agents that capture leads, automate conversations and close sales — 24/7.
            </p>
            <p className={styles.footerBrandProductOf}>
              A product of <strong>Provaantech</strong>
            </p>
          </div>

          {/* Brand System center */}
          <div className={styles.footerLinkColumn}>
            <h4 className={styles.footerColHeader}>Brand System</h4>
            <ul className={styles.footerLinksList}>
              <li className={styles.footerLinkItem}>
                <FiMessageCircle style={{ marginRight: '0.5rem' }} /> Sailsense Chatbot
              </li>
              <li className={styles.footerLinkItem}>
                <FiPhone style={{ marginRight: '0.5rem' }} /> ROVIA — Voice AI
              </li>
              <li className={styles.footerLinkItem}>
                <FiVideo style={{ marginRight: '0.5rem' }} /> AIMEE — Video AI
              </li>
              <li className={styles.footerLinkItem}>
                <FiSettings style={{ marginRight: '0.5rem' }} /> Sailsense Platform
              </li>
            </ul>
          </div>

          {/* Industries right */}
          <div className={styles.footerLinkColumn}>
            <h4 className={styles.footerColHeader}>Focus Industry</h4>
            <ul className={styles.footerLinksList}>
              <li className={styles.footerLinkItem}>Automotive Dealerships</li>
            </ul>
          </div>
        </div>

        {/* Bottom row copyright */}
        <div className={styles.footerBottomBar}>
          <div>© 2026 Sailsense AI - Provaantech. All rights reserved.</div>
          <div>Built for high-performance sales teams.</div>
        </div>
      </footer>
    </div>
  );
};

export default AIAgentsPage;
